import { Link } from 'react-router-dom';
import { CalendarCheck, Video, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ConsultationBooking } from '@/api/consultationsApi';
import { httpsUrl } from '@/lib/safeUrl';

/** Shown straight after payment. The link lives here and in booking history,
 *  so a lost email never costs someone their appointment. */
export function ConsultationConfirmed({ booking }: { booking: ConsultationBooking }) {
  const joinUrl = httpsUrl(booking.meet_link);
  const ready = !!joinUrl;
  return (
    <div className="mx-auto max-w-lg py-10 text-center">
      <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/10">
        <CalendarCheck className="size-7 text-primary" />
      </span>
      <h1 className="mt-5 text-2xl font-bold tracking-tight text-foreground">
        Your consultation is booked
      </h1>
      <p className="mt-2 text-muted-foreground">
        {booking.scheduled_date} at {booking.scheduled_time} IST, with{' '}
        {booking.consultant_name || 'a Recovry physiotherapist'}.
      </p>

      <div className="mt-6 rounded-2xl border bg-card p-5 text-left">
        <dl className="space-y-2 text-sm">
          {[
            ['Reference', booking.consultation_id],
            ['Length', `${booking.session_minutes} minutes`],
            ['Paid', `₹${booking.total_amount}`],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-medium text-foreground">{value}</dd>
            </div>
          ))}
        </dl>

        {ready ? (
          <Button className="mt-5 w-full" asChild>
            <a href={joinUrl ?? '#'} target="_blank" rel="noopener noreferrer">
              <Video className="size-4" /> Join the session
            </a>
          </Button>
        ) : (
          <p className="mt-5 flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            <Mail className="mt-0.5 size-4 shrink-0" />
            Your joining link is being set up. It will be emailed to you and will appear on this
            booking within a few minutes.
          </p>
        )}
      </div>

      <p className="mt-5 text-sm text-muted-foreground">
        We have emailed the details. You can also find this under{' '}
        <Link to="/history" className="text-primary hover:underline">
          your bookings
        </Link>
        .
      </p>
    </div>
  );
}
